/* Admin · Getting Started ebook 7-day survey dashboard.
 *
 * Three blocks:
 *   1. Health strip — invited / answered / response_rate / "Trigger sweep" button
 *   2. Top keyword themes — cheap-and-cheerful word-frequency cloud
 *   3. Newest responses (200 most recent), one row per answer
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, RefreshCw, Send, MessageSquare } from "lucide-react";
import { toast } from "sonner";

export default function AdminEbookSurvey() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sweeping, setSweeping] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/ebook-survey/responses");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load responses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const triggerSweep = async () => {
    setSweeping(true);
    try {
      const r = await api.post("/admin/ebook-survey/trigger-sweep");
      const { checked, sent, errors } = r.data;
      toast.success(`Sweep complete · checked=${checked} sent=${sent} errors=${errors}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sweep failed");
    } finally {
      setSweeping(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="animate-spin text-emerald-600" />
      </div>
    );
  }

  const stats = data?.stats || {};
  const rows = data?.rows || [];
  const topKw = data?.top_keywords || [];

  return (
    <div className="flex flex-col gap-6" data-testid="admin-ebook-survey">
      {/* Header + actions */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-extrabold text-stone-900 leading-tight">
            Ebook Survey · Day-7 voices
          </h1>
          <p className="text-stone-500 text-sm mt-0.5">
            Every reader of &ldquo;Getting Started&rdquo; gets a 1-question survey 7 days after download. Their answers land here.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={triggerSweep}
            disabled={sweeping}
            className="btn btn-money flex items-center gap-1.5"
            data-testid="admin-ebook-survey-trigger-sweep"
            title="Manually fire the 7-day cohort email send right now"
          >
            {sweeping ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Trigger sweep
          </button>
          <button
            type="button"
            onClick={load}
            className="btn btn-ghost flex items-center gap-1.5"
            data-testid="admin-ebook-survey-refresh"
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {/* Health strip */}
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Invited" value={stats.invited ?? 0} testId="ebook-survey-stat-invited" />
        <Stat label="Answered" value={stats.answered ?? 0} accent testId="ebook-survey-stat-answered" />
        <Stat label="Response rate" value={`${stats.response_rate_pct ?? 0}%`} testId="ebook-survey-stat-rate" />
      </div>

      {/* Top keywords */}
      {topKw.length > 0 && (
        <div className="card" data-testid="ebook-survey-top-keywords">
          <div className="label-eyebrow mb-2 text-emerald-700">Top themes</div>
          <div className="flex flex-wrap gap-2">
            {topKw.map((k) => (
              <span
                key={k.word}
                className="px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 font-semibold text-xs"
                style={{ fontSize: `${Math.min(18, 11 + k.count)}px` }}
              >
                {k.word} <span className="text-emerald-600 tabular-nums opacity-60">×{k.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Responses list */}
      <div className="card" data-testid="ebook-survey-responses-list">
        <div className="flex items-center gap-2 mb-3">
          <MessageSquare size={14} className="text-stone-500" />
          <h2 className="font-bold text-stone-900">
            Newest {rows.length} {rows.length === 1 ? "response" : "responses"}
          </h2>
        </div>
        {rows.length === 0 ? (
          <div className="text-sm text-stone-500 py-8 text-center">
            No survey responses yet. Once the first 7-day cohort answers, they&apos;ll appear here.
          </div>
        ) : (
          <ul className="divide-y divide-stone-100">
            {rows.map((r) => (
              <li
                key={r.id}
                className="py-3"
                data-testid={`ebook-survey-row-${r.id}`}
              >
                <div className="flex items-baseline gap-2 mb-1 flex-wrap">
                  <span className="font-bold text-stone-900 text-sm">{r.name || "—"}</span>
                  <span className="text-xs text-stone-500">{r.email}</span>
                  {r.state && <span className="text-[10px] uppercase tracking-wider text-stone-400">· {r.state}</span>}
                  <span className="text-[10px] uppercase tracking-wider text-stone-400 ml-auto">
                    {r.survey_answered_at ? new Date(r.survey_answered_at).toLocaleDateString() : "—"}
                  </span>
                </div>
                <div className="text-sm text-stone-700 leading-relaxed pl-1 border-l-2 border-emerald-200 ml-1 italic">
                  &ldquo;{r.survey_answer}&rdquo;
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent, testId }) {
  return (
    <div
      className={"card text-center " + (accent ? "border-emerald-300 bg-emerald-50" : "")}
      data-testid={testId}
    >
      <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">{label}</div>
      <div className={"font-display font-extrabold text-3xl tabular-nums " + (accent ? "text-emerald-700" : "text-stone-900")}>
        {value}
      </div>
    </div>
  );
}
